import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';

/**
 * Permisos del panel admin, derivados del mismo menú que pinta el sidebar (`POST /menu`, que sale
 * de `menu` + `menu_rol` + `user_roles`). Si una URL está en el menú del usuario, puede entrar a ella.
 *
 * El menú se pide una sola vez por usuario y sesión (shareReplay) y lo comparten el sidebar, el
 * guard `adminPermisosGuard` y el botón "Administrar" del perfil. Si cambian los roles/menús de un
 * usuario, lo verá al recargar la página o al volver a iniciar sesión.
 *
 * OJO: esto solo protege la navegación del frontend. La API sigue aceptando cualquier token válido
 * hasta que se agregue la validación de permisos en el backend.
 */
@Injectable({ providedIn: 'root' })
export class AdminPermisosService {
  private authService = inject(AuthService);

  private menus = new Map<number, Observable<any>>();

  constructor() {
    // Al cerrar sesión (setUser(null)) se descarta el menú cacheado.
    this.authService.user$.subscribe(user => {
      if (!user) this.limpiar();
    });
  }

  /** Respuesta de `/menu` ({success, data}) cacheada por usuario. */
  getMenu(userId: number): Observable<any> {
    if (!this.menus.has(userId)) {
      this.menus.set(userId, this.authService.getMenu(userId).pipe(
        // Si falló (sin conexión y sin copia local), no se deja cacheado: se reintenta la próxima vez.
        tap((response: any) => { if (!response?.success) this.menus.delete(userId); }),
        shareReplay(1)
      ));
    }
    return this.menus.get(userId)!;
  }

  /** URLs permitidas para el usuario (ítems sueltos + submenús de cada grupo). */
  getUrlsPermitidas(userId: number): Observable<string[]> {
    return this.getMenu(userId).pipe(
      map((response: any) => {
        if (!response?.success || !Array.isArray(response.data)) return [];

        const urls: string[] = [];
        for (const item of response.data) {
          if (item.group === 'true') {
            (item.submenu ?? []).forEach((sub: any) => urls.push(this.normalizar(sub.url)));
          } else {
            urls.push(this.normalizar(item.url));
          }
        }
        return urls.filter(url => url && url !== '/' && url !== '/#');
      })
    );
  }

  /** `true` si el usuario tiene al menos una página del admin en su menú. */
  tieneAccesoAdmin(userId: number): Observable<boolean> {
    return this.getUrlsPermitidas(userId).pipe(map(urls => urls.length > 0));
  }

  /**
   * Una ruta está permitida si coincide con una URL del menú o cuelga de ella por segmento:
   * `/organizaciones` habilita `/organizaciones/15`, pero `/admin-menu` no habilita `/admin-menu-rol`.
   */
  rutaPermitida(ruta: string, urls: string[]): boolean {
    const r = this.normalizar(ruta);
    return urls.some(url => r === url || r.startsWith(url + '/'));
  }

  /**
   * Para rutas que no están en el menú pero pertenecen a un módulo (`data.permiso`): basta con
   * tener cualquier página de ese módulo. Ej. `permiso: '/builder-formularios'` lo cumple quien
   * tenga `/builder-formularios/biblioteca`.
   */
  moduloPermitido(modulo: string, urls: string[]): boolean {
    const m = this.normalizar(modulo);
    return urls.some(url => url === m || url.startsWith(m + '/') || m.startsWith(url + '/'));
  }

  limpiar(): void {
    this.menus.clear();
  }

  private normalizar(url: string): string {
    if (!url) return '';
    let limpia = url.trim().split(/[?#]/)[0];
    if (!limpia.startsWith('/')) limpia = '/' + limpia;
    if (limpia.length > 1 && limpia.endsWith('/')) limpia = limpia.slice(0, -1);
    return limpia;
  }
}
